import { captureException } from '@restworld/utils-common';

/**
 * The `error_kind` values this feature reports, in one place so the strings that O2 filters on
 * cannot drift apart from each other.
 *
 * Passed as `kind`, which is the ONLY way to reach the `error_kind` column: the RUM package
 * fills that column from the error's `name`, never from the context. Anything put in the
 * context travels as an ordinary custom attribute. That is precisely why `error_kind` is empty
 * on 50.777 errors out of 50.777 on the `candidature` pages today, and why the only frontend
 * alert is switched off: the existing calls put their name in the context, under `reason`.
 *
 * `captureException` learns to move `kind` onto the error's `name` in PR #409. Until that is
 * merged these three travel as a plain attribute, which breaks nothing and starts working the
 * day #409 lands.
 *
 * None of these change what the worker sees. They are added next to an outcome that was already
 * decided, never in place of one.
 */
export const IN_APP_ASSESSMENT_ERROR_KIND = {
  /**
   * The navigation into the chat did not happen: it threw, the promise rejected, or Next
   * cancelled the route and `router.push` resolved `false`. One name for all three because
   * they are one event for the worker, and `trigger` and `outcome` keep them apart inside it.
   *
   * This is the worst thing this feature can do to a person: a modal with no X, no Escape and
   * no backdrop, and the only control on it is the button that just failed. The code answers by
   * giving the close controls back (see `InAppAssessmentReturningConfirmation`); this counts
   * how often that happens, because if it happens at all we need to know.
   */
  navigationFailed: 'serena_modal_navigation_failed',
  /**
   * The interview confirmation was drawn without a job position to send the worker to, so the
   * only button on it leads nowhere. Same trap as above, one step earlier.
   */
  interviewHrefMissing: 'serena_modal_interview_href_missing',
  /**
   * The application came back without a usable `assessment_channel`. Not fatal, and the
   * fallback (today's confirmation) is the right outcome, but if it happens a lot the gate is
   * not working and nobody would notice.
   */
  channelMissing: 'serena_modal_channel_missing',
} as const;

export const reportInterviewNavigationFailed = (
  error: unknown,
  context: {
    jobPositionId?: string;
    /** Whether the countdown or the worker started it. */
    trigger: 'countdown' | 'button';
    /** `cancelled` is `router.push` resolving `false`, which throws nothing at all. */
    outcome: 'threw' | 'rejected' | 'cancelled';
  }
) =>
  captureException(error, {
    kind: IN_APP_ASSESSMENT_ERROR_KIND.navigationFailed,
    job_position_id: context.jobPositionId,
    trigger: context.trigger,
    outcome: context.outcome,
  });

export const reportInterviewHrefMissing = () =>
  captureException(new Error('The in-app interview confirmation has no job position to link to'), {
    kind: IN_APP_ASSESSMENT_ERROR_KIND.interviewHrefMissing,
  });

export const reportAssessmentChannelMissing = (context: {
  channel: unknown;
  /** Which of the two confirmations was deciding: the returning worker's, or the CV flow's. */
  surface: 'job_apply_confirmation' | 'cv_upload_flow';
  jobPositionId?: string;
}) =>
  captureException(new Error('The created application carries no usable assessment channel'), {
    kind: IN_APP_ASSESSMENT_ERROR_KIND.channelMissing,
    assessment_channel: context.channel === undefined ? 'absent' : String(context.channel),
    surface: context.surface,
    job_position_id: context.jobPositionId,
  });
